const STORAGE_KEY = 'chat_messages';
const STATE_KEY = 'chat_ui_state';

function loadFromStorage() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveToStorage(messages) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
    }
}

function saveChatState(state) {
    try {
        localStorage.setItem(STATE_KEY, JSON.stringify({
            isOpen: state.isOpen
        }));
    } catch {
    }
}

function loadChatState() {
    try {
        const stored = localStorage.getItem(STATE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
}

const chatModule = {
    namespaced: true,

    state: () => {
        const savedState = loadChatState();
        return {
            messages: loadFromStorage(),
            isOpen: savedState.isOpen || false,
            isLoading: false,
            error: null,
            isInitialized: false,
            saveStatus: null,
        };
    },

    mutations: {
        SET_MESSAGES(state, messages) {
            state.messages = messages;
            saveToStorage(messages);
        },
        ADD_MESSAGE(state, message) {
            state.messages.push(message);
            saveToStorage(state.messages);
        },
        CLEAR_MESSAGES(state) {
            state.messages = [];
            saveToStorage([]);
        },
        SET_LOADING(state, loading) {
            state.isLoading = loading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_OPEN(state, isOpen) {
            state.isOpen = isOpen;
            saveChatState(state);
        },
        SET_INITIALIZED(state, initialized) {
            state.isInitialized = initialized;
        },
        SET_SAVE_STATUS(state, status) {
            state.saveStatus = status;
        },
    },

    actions: {
        async sendMessage({ commit, state }, content) {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            commit('SET_SAVE_STATUS', 'saving');

            const tempId = 'temp-' + Date.now();

            commit('ADD_MESSAGE', {
                id: tempId,
                role: 'user',
                content,
                created_at: new Date().toISOString(),
            });

            try {
                const response = await axios.post('/api/chat', { content });

                const persistedUserMessage = {
                    id: response.data.user_message_id ?? tempId,
                    role: 'user',
                    content,
                    created_at: new Date().toISOString(),
                };

                const assistantMessage = {
                    id: response.data.message_id,
                    role: 'assistant',
                    content: response.data.reply,
                    tool_used: response.data.tool_used,
                    query_duration_ms: response.data.query_duration_ms,
                    created_at: new Date().toISOString(),
                };

                const idx = state.messages.findIndex(m => m.id === tempId);
                if (idx !== -1) {
                    commit('SET_MESSAGES', [
                        ...state.messages.slice(0, idx),
                        persistedUserMessage,
                        ...state.messages.slice(idx + 1),
                        assistantMessage,
                    ]);
                } else {
                    commit('ADD_MESSAGE', persistedUserMessage);
                    commit('ADD_MESSAGE', assistantMessage);
                }

                commit('SET_SAVE_STATUS', 'saved');
                setTimeout(() => commit('SET_SAVE_STATUS', null), 2500);

                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.error ||
                    error.response?.data?.reply ||
                    'Failed to send message. Please try again.';

                commit('SET_ERROR', errorMessage);

                const idx = state.messages.findIndex(m => m.id === tempId);
                if (idx !== -1) {
                    commit('SET_MESSAGES', [
                        ...state.messages.slice(0, idx),
                        ...state.messages.slice(idx + 1),
                        {
                            id: 'error-' + Date.now(),
                            role: 'assistant',
                            content: errorMessage,
                            is_error: true,
                            created_at: new Date().toISOString(),
                        },
                    ]);
                } else {
                    commit('ADD_MESSAGE', {
                        id: 'error-' + Date.now(),
                        role: 'assistant',
                        content: errorMessage,
                        is_error: true,
                        created_at: new Date().toISOString(),
                    });
                }

                commit('SET_SAVE_STATUS', null);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async fetchHistory({ commit }) {
            commit('SET_LOADING', true);

            try {
                const response = await axios.get('/api/chat/history');
                commit('SET_MESSAGES', response.data.messages || []);
                commit('SET_INITIALIZED', true);
            } catch (error) {
                console.error('Failed to fetch chat history:', error);
                commit('SET_INITIALIZED', true);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async clearHistory({ commit }) {
            try {
                await axios.delete('/api/chat/history');
                commit('CLEAR_MESSAGES');
                commit('SET_INITIALIZED', false);
            } catch (error) {
                console.error('Failed to clear chat history:', error);
                throw error;
            }
        },

        startNewChat({ commit, dispatch }) {
            commit('CLEAR_MESSAGES');
            commit('SET_INITIALIZED', false);
            dispatch('clearHistory').catch(() => {});
        },

        toggleChat({ commit, state }) {
            commit('SET_OPEN', !state.isOpen);
        },

        openChat({ commit }) {
            commit('SET_OPEN', true);
        },

        closeChat({ commit }) {
            commit('SET_OPEN', false);
        },
    },

    getters: {
        hasMessages: (state) => state.messages.length > 0,
        lastMessage: (state) => state.messages[state.messages.length - 1],
        isPanelOpen: (state) => state.isOpen,
        isChatLoading: (state) => state.isLoading,
        chatError: (state) => state.error,
        saveStatus: (state) => state.saveStatus,
    },
};

export default chatModule;
